import { Fragment, useEffect, useState } from 'react';
import { connectWithViewport, getStore } from 'app/hermes-redux';

import { getBrandedData } from 'stores/company-store';
import { goTo } from 'router/navigation';
import cloudinary from 'globals/cloudinary';
import { updateLanguage } from 'app/state/session/actions';
import Language, { supportedLanguages } from 'language';
import { getLocaleBrandingLogo } from 'globals/helpers';
import LanguageSelector from 'wa-storybook/components/general/language-selector/language-selector';
import Button from 'wa-storybook/components/general/button/button';
import type { AppState, Dispatch } from 'globals/types.js';
import { signupStates } from 'router/constants';
import { Link, useSearchParams } from 'react-router-dom';
import styles from './header-logged-out.module.scss';
import { getInitDataForCode } from 'pages/entry/invitation-code-form/invitation-code-form';
import { updateData } from 'pages/signup/main/state/actions';
import isEmpty from 'lodash/isEmpty';
import type { Model as CompanyModel } from 'state/company/types';
import { useThemeContext } from 'globals/hooks/use-dark-mode';
import { useRouterState } from 'router/router-state-provider';
import type { Module } from 'router/types';

type Props = {
  dispatch: Dispatch;
  hideActions: boolean;
  isMobile: boolean;
  company: CompanyModel;
};

const statesWithLoginPageButton = {
  signupInvitation: signupStates.signupInvitation,
  resendInvitation: signupStates.resendInvitation,
  resendInvitationSuccess: signupStates.resendInvitationSuccess,
} as const;

export const isSignupState = (stateName: string) => {
  return Object.keys(signupStates).includes(stateName);
};

type Params = {
  lang: string;
  code?: string;
};

export const handleLangChange = async (
  lang: {
    value: string;
  },
  dispatch: Dispatch,
  setLocale: (locale: string) => void,
  currentState: Module,
  invitationCode?: string | null,
) => {
  const {
    signup: { submissionModel },
  } = getStore().getState();

  await dispatch(updateLanguage(lang.value, false));
  setLocale(Language.getLocale());

  // localised password requirements come from back end then are stored in state
  if (submissionModel.company_invitation_code) {
    const result = await getInitDataForCode(
      submissionModel.company_invitation_code,
      submissionModel.otp,
    );

    const body = result?.body;

    if (body) {
      dispatch(
        updateData('passwordRequirements', [...body['password_requirements']]),
      );
    }
  }

  if (isSignupState(currentState.name)) {
    const params: Params = {
      lang: lang.value,
    };

    if (invitationCode && currentState.name !== signupStates.signup) {
      params.code = invitationCode;
      return goTo(signupStates.signupInvitation, params, { reload: true });
    }
  }
};

export const HeaderLoggedOut = (props: Props) => {
  const { isDark } = useThemeContext();
  const [locale, setLocale] = useState(Language.getLocale());
  const { hideActions, isMobile, company } = props;
  const { currentRoute } = useRouterState();
  const [searchParams] = useSearchParams();

  const currentStateName = currentRoute?.name || '/';

  const fallbackLogo = getLocaleBrandingLogo(isDark);

  const imgSrc = !isEmpty(getBrandedData('image_square_logo'))
    ? cloudinary(getBrandedData('image_square_logo'), 'h_100')
    : fallbackLogo;

  useEffect(() => {
    // language can be changed from the cookies modal
    // make sure we update LanguageSelector
    setLocale(Language.getLocale());
  }, [Language.getLocale()]);

  const loginPageButton = () =>
    Object.keys(statesWithLoginPageButton).includes(currentStateName) && (
      <div className={styles['login-page-button']}>
        <Button
          dataHook='log-in-button'
          text={polyglot.t('components.header.login')}
          onClick={() => goTo('/')}
        />
      </div>
    );

  const signUpActions = () =>
    !hideActions &&
    !isSignupState(currentStateName) && (
      <Fragment>
        <div className={styles['invitation-code']}>
          {polyglot.t('components.header.invitation_link')}
        </div>
        <div className={styles.button} id='signup-button'>
          <Button
            dataHook='sign-up-button'
            size='large'
            text={polyglot.t('sign_up.form_comp.cta')}
            onClick={() => goTo(signupStates.signupInvitation)}
          />
        </div>
      </Fragment>
    );

  return (
    <div className={styles.header}>
      <div className={styles['header__mobile-top']}>
        <Link to={hideActions ? '' : '/'} className={styles['logo__wrapper']}>
          <img
            src={imgSrc}
            className={styles.logo}
            alt={polyglot.t('components.main_menu.home_page')}
          />
        </Link>
        <div className={styles.right}>
          {!isMobile && loginPageButton()}
          {!isMobile && signUpActions()}
          <div className={styles.language}>
            <LanguageSelector
              languages={supportedLanguages}
              companyLocale={company.locale}
              selectedLanguage={locale}
              onChange={newLocale => {
                const lang = {
                  value: newLocale.value || '',
                } as const;
                handleLangChange(
                  lang,
                  props.dispatch,
                  setLocale,
                  currentRoute,
                  searchParams.get('code'),
                );
              }}
              onClick={() => Language.setLoginLanguagePersistence(true)}
              small
            />
          </div>
        </div>
      </div>
      {isMobile && (
        <div className={styles['header__mobile-bottom']}>
          {loginPageButton()}
          {signUpActions()}
        </div>
      )}
    </div>
  );
};

HeaderLoggedOut.displayName = 'HeaderLoggedOut';

// connecting with viewport so that
// strings update after language change
export default connectWithViewport(
  HeaderLoggedOut,
  ({ company }: AppState) => ({
    company,
  }),
);
